import {
  ChangeEvent,
  FC,
  Ref,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';

import {
  addressAsString,
  AddressAutocomplete,
  Button,
  Icon,
  ILocalAddress,
  Input,
  Spinner,
  useData,
} from '@/components';
import { useEcomStoreSelector } from '@/data';
import { useStoreHasConfiguredDeliveryZones } from '@/hooks';
import {
  getDeliveryProviderId,
  getOverlappingDeliveryProviderIds,
  isDeliveryAddressOutOfZone,
  resolveStoreDeliverySchedule,
  TreezAndExternalStore,
} from '@/utils';

import { useStoreLocatorContext } from './context';
import {
  CURRENT_LOCATION_LABEL,
  OUT_OF_DELIVERY_ZONE_MESSAGE,
} from './constants';

import { StoresList } from './StoresList.components';
import styles from './storelocator.module.scss';

interface IDeliverySectionProps {
  handleUseCurrentLocation: () => void;
  loadingLocation: boolean;
  showInstructionText: boolean;
  ref?: Ref<HTMLInputElement | HTMLTextAreaElement> | null;
}

const ADDRESS_INPUT_ID = 'addres_input';

const DeliverySection: FC<IDeliverySectionProps> = ({
  showInstructionText,
  loadingLocation,
  handleUseCurrentLocation,
  ref: inputRef,
}) => {
  const {
    isDeliveryAddressInvalid,
    setDeliveryAddress,
    deliveryAddress,
    singleStore,
    isPickup,
    setIsDeliveryAddressInvalid,
    selectedStoreId,
    setSelectedStoreId,
    deliverySectionTitle,
    enableDeliveryZoneRouting,
  } = useStoreLocatorContext();
  const { store, treezStores } = useData();
  const { treez_store_name } = useEcomStoreSelector(['treez_store_name']);
  const hasConfiguredDeliveryZones = useStoreHasConfiguredDeliveryZones(store);

  const [overlappingProviderIds, setOverlappingProviderIds] = useState<
    string[]
  >([]);

  useEffect(() => {
    if (!enableDeliveryZoneRouting || !hasConfiguredDeliveryZones) return;

    if (!deliveryAddress?.address1 || !deliveryAddress?.zip) {
      setOverlappingProviderIds([]);
      return;
    }

    const providerIds = getOverlappingDeliveryProviderIds(
      deliveryAddress,
      store,
      treezStores,
    );

    setOverlappingProviderIds(providerIds.length > 1 ? providerIds : []);
  }, [
    deliveryAddress,
    enableDeliveryZoneRouting,
    hasConfiguredDeliveryZones,
    store,
    treezStores,
  ]);

  const deliveryStoresForPicker = useMemo((): TreezAndExternalStore[] => {
    if (!overlappingProviderIds.length) return [];

    const stores = overlappingProviderIds
      .map(providerId => treezStores?.find(s => s.short_name === providerId))
      .filter(Boolean) as TreezAndExternalStore[];

    const selected = stores.find(s => s.short_name === treez_store_name);

    return selected
      ? [selected, ...stores.filter(s => s.short_name !== treez_store_name)]
      : stores;
  }, [overlappingProviderIds, treez_store_name, treezStores]);

  const applyDeliverySelection = useCallback(
    (nextAddress: ILocalAddress, providerStoreId: string | undefined) => {
      if (!providerStoreId) {
        setIsDeliveryAddressInvalid(true);
        setDeliveryAddress(nextAddress);
        setSelectedStoreId(undefined as unknown as string);
        return;
      }

      setIsDeliveryAddressInvalid(false);
      setDeliveryAddress(nextAddress);
      setSelectedStoreId(providerStoreId);
    },
    [setDeliveryAddress, setIsDeliveryAddressInvalid, setSelectedStoreId],
  );

  const onAddressChange = (address: ILocalAddress) => {
    if (!address) {
      setIsDeliveryAddressInvalid(false);
      setOverlappingProviderIds([]);
      return;
    }

    if (!enableDeliveryZoneRouting || !hasConfiguredDeliveryZones) {
      setOverlappingProviderIds([]);

      const { hasConfiguredZones, schedule } = resolveStoreDeliverySchedule(
        address,
        store,
        treezStores,
      );

      if (!hasConfiguredZones) {
        setIsDeliveryAddressInvalid(false);
        setDeliveryAddress(address);
        setSelectedStoreId(schedule!.stores_provider_id);
        return;
      }

      if (!schedule) {
        setIsDeliveryAddressInvalid(true);
        setDeliveryAddress(address);
        setSelectedStoreId(undefined as unknown as string);
        return;
      }

      setIsDeliveryAddressInvalid(false);
      setDeliveryAddress(address);
      setSelectedStoreId(schedule.stores_provider_id);
      return;
    }

    const providerIds = getOverlappingDeliveryProviderIds(
      address,
      store,
      treezStores,
    );

    if (!providerIds.length) {
      setOverlappingProviderIds([]);
      applyDeliverySelection(address, undefined);
      return;
    }

    setOverlappingProviderIds(providerIds.length > 1 ? providerIds : []);

    applyDeliverySelection(
      address,
      getDeliveryProviderId(providerIds, selectedStoreId),
    );
  };

  const handleOverlappingStoreSelected = useCallback(
    (providerStoreId?: string) => {
      if (!providerStoreId || !deliveryAddress) return;
      if (!overlappingProviderIds.includes(providerStoreId)) return;

      applyDeliverySelection(deliveryAddress, providerStoreId);
    },
    [applyDeliverySelection, deliveryAddress, overlappingProviderIds],
  );

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    setDeliveryAddress({
      ...deliveryAddress,
      address2: event?.target?.value ?? '',
    });
  };
  const isShownAddressOutOfZone = isDeliveryAddressOutOfZone(
    deliveryAddress,
    store,
    treezStores,
  );

  return (
    <div
      className={classNames(styles.delivery_section, {
        [styles.delivery_section_zone_routing]: enableDeliveryZoneRouting,
      })}
    >
      <Button
        ariaLabel={CURRENT_LOCATION_LABEL}
        className={classNames(styles.current_location, {
          [styles.hidden]: singleStore && isPickup,
        })}
        onClick={handleUseCurrentLocation}
      >
        {loadingLocation ? (
          <Spinner className={styles.crosshairs_icon} sm />
        ) : (
          <Icon className={styles.crosshairs_icon} name="crosshairs" />
        )}
        <span>{CURRENT_LOCATION_LABEL}</span>
      </Button>
      {showInstructionText && (
        <p className={styles.delivery_section__title}>{deliverySectionTitle}</p>
      )}
      <div className={styles.delivery_section__form}>
        <AddressAutocomplete
          feedback={OUT_OF_DELIVERY_ZONE_MESSAGE}
          id={ADDRESS_INPUT_ID}
          isClearable={!!addressAsString(deliveryAddress)}
          isInvalid={
            (isDeliveryAddressInvalid || isShownAddressOutOfZone) &&
            hasConfiguredDeliveryZones
          }
          isSmall
          label="Your address"
          name={ADDRESS_INPUT_ID}
          onAddressChange={onAddressChange}
          onAddressClear={() => {
            setIsDeliveryAddressInvalid(false);
            setSelectedStoreId(undefined as unknown as string);
            setOverlappingProviderIds([]);
            setDeliveryAddress(undefined);
          }}
          placeholder="Your address"
          ref={inputRef}
          value={deliveryAddress ? addressAsString(deliveryAddress) ?? '' : ''}
        />
        <Input
          inputLabel="Apartment or suite"
          isSmall
          name="address2"
          onChange={handleChange}
          placeholder="Apartment or suite (optional)"
          value={deliveryAddress ? deliveryAddress?.address2 ?? '' : ''}
        />
      </div>

      {enableDeliveryZoneRouting &&
        hasConfiguredDeliveryZones &&
        overlappingProviderIds.length > 1 && (
          <div className={styles.delivery_section__content}>
            <StoresList
              emptyNoStoresLabel="No stores available for this address"
              hideCurbside
              loadingLocation={loadingLocation}
              onSelectedStoreId={handleOverlappingStoreSelected}
              panelId="delivery-overlap-store-list-panel"
              stores={deliveryStoresForPicker}
            />
          </div>
        )}
    </div>
  );
};

export { DeliverySection };
